import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Typography, Button, Grid } from "@mui/material";
import Modal from "../../../../components/Modal/Modal";
import { useTranslation } from "react-i18next";
import PolicyForm from "../../../../components/Forms/PolicyForm";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import { useCourseVersions } from "../../../../hooks/courseVersions";
import { useSnackbarNotifications } from "../../../../hooks/snackbar";
import { some } from "lodash";

const selector = makeGetEntities();

const AddPolicyVersionModalForm = ({
  submitForm,
  onClose,
  open,
  isSubmitting,
  courseTitle,
  resetForm,
  formFields,
  copyFromExisting
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    resetForm();
  }, [open]);

  return (
    <Modal
      maxWidth="sm"
      fullWidth
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {`${courseTitle}: ${t("manageCourses.courseVersionsAddNewTitle")}`}
        </Typography>
      }
      dialogContent={
        <Grid container spacing={2}>
          {copyFromExisting && (
            <Grid item xs={12}>
              {formFields.version}
            </Grid>
          )}
          <Grid item xs={12}>
            {formFields.name}
          </Grid>
          <Grid item xs={12}>
            {formFields.number}
          </Grid>
          <Grid item xs={12}>
            {formFields.description}
          </Grid>
          <Grid item xs={12} sm={6}>
            {formFields.language}
          </Grid>
          <Grid item xs={12} sm={6}>
            {formFields.status}
          </Grid>
          <Grid item xs={12} sm={6}>
            {formFields.trainingHours}
          </Grid>
          <Grid item xs={12} sm={6}>
            {formFields.trainingMinutes}
          </Grid>
        </Grid>
      }
      dialogActions={
        <>
          <Button color="primary" disabled={isSubmitting} onClick={onClose}>
            {t("manageCourses.courseVersionsAddNewCancelBtn")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submitForm}
            disabled={isSubmitting}
          >
            {t("manageCourses.courseVersionsAddNewSubmitBtn")}
          </Button>
        </>
      }
      open={open}
      onClose={onClose}
      scrollable
    />
  );
};

const AddPolicyVersionModal = ({
  onClose,
  open,
  onSubmit,
  courseId,
  copyFromExisting
}) => {
  const [versionsMenuItems, setVersionsMenuItems] = useState([]);
  const [activeVersion, setActiveVersion] = useState(null);
  const { courseSettings } = useSelector(({ contentCoursesState }) => ({
    courseSettings: selector(contentCoursesState, {
      type: "courseSettings",
      id: courseId
    })
  }));
  const { t: globalT } = useTranslation();
  const { displayErrorMessage } = useSnackbarNotifications();

  const courseTitle = courseSettings ? courseSettings.name : "";
  const courseDescription = courseSettings ? courseSettings.description : "";
  const courseHours = courseSettings ? courseSettings.durationHours : 0;
  const courseMinutes = courseSettings ? courseSettings.durationMinutes : 0;
  const { courseVersions } = useCourseVersions(courseId, "all");

  const formValues = {
    name: courseTitle,
    number: "",
    trainingHours: courseHours,
    trainingMinutes: courseMinutes,
    language: "English",
    status: "Active",
    description: courseDescription,
    version: copyFromExisting
      ? courseVersions?.length === 1
        ? courseVersions[0].id
        : ""
      : ""
  };

  const [initialValues, setInitialValues] = useState(formValues);

  const newInitialValues = sourceVersion => {
    return {
      name: sourceVersion.name,
      number: sourceVersion.number,
      trainingHours: sourceVersion.trainingHours,
      trainingMinutes: sourceVersion.trainingMinutes,
      language: sourceVersion.language,
      status: sourceVersion.status,
      description: sourceVersion.description,
      version: sourceVersion.id
    };
  };

  useEffect(() => {
    const versionsList = [];
    setActiveVersion(null);
    setInitialValues(formValues);
    if (!copyFromExisting || !courseVersions?.length) return;
    courseVersions.forEach(courseVersion => {
      versionsList.push({
        value: courseVersion.id,
        label: courseVersion.versionLabel
      });
    });
    setVersionsMenuItems(versionsList);
    if (courseVersions.length === 1) {
      setInitialValues(newInitialValues(courseVersions[0]));
      setActiveVersion(courseVersions[0].id);
    }
  }, [open, courseVersions]);

  const handleChangeVersion = versionId => {
    const activeVersion = courseVersions?.find(
      courseVersion => courseVersion.id == versionId
    );
    if (activeVersion) {
      setActiveVersion(versionId);
      setInitialValues(newInitialValues(activeVersion));
    }
  };

  const parseResponseErrors = errors => {
    if (
      some(
        errors,
        error => error.detail === "Number must be unique within manual"
      )
    ) {
      return { number: "managePolicies.error.duplicateNumber" };
    }
    return false;
  };

  const onSubmitPolicyVersion = async (
    values,
    setSubmitting,
    setFieldError
  ) => {
    try {
      const response = await onSubmit(values);
      if (response.errors) {
        const parsedErrors = parseResponseErrors(response.errors);
        if (parsedErrors.number) {
          setFieldError("number", globalT(parsedErrors.number));
          setSubmitting(false);
        } else {
          throw response.errors;
        }
      }
    } catch (e) {
      console.error(e);
      displayErrorMessage(globalT("managePolicies.error.create"));
    }
  };

  return (
    <PolicyForm
      addNewVersion={true}
      versionsMenuItems={versionsMenuItems}
      copyFromExisting={copyFromExisting}
      activeVersion={activeVersion}
      handleChangeVersion={handleChangeVersion}
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting, setFieldError }) => {
        setSubmitting(true);
        onSubmitPolicyVersion(values, setSubmitting, setFieldError);
      }}
      render={({
        formFields,
        formikProps: { resetForm, submitForm, isSubmitting }
      }) => (
        <AddPolicyVersionModalForm
          submitForm={submitForm}
          isSubmitting={isSubmitting}
          resetForm={resetForm}
          open={open}
          onClose={onClose}
          courseTitle={courseTitle}
          formFields={formFields}
          copyFromExisting={copyFromExisting}
        />
      )}
    />
  );
};

AddPolicyVersionModal.propTypes = {
  /**
   * Called to close the Add Course Version Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to create the new version
   */
  onSubmit: PropTypes.func
};

export default AddPolicyVersionModal;
